import { HOSPITAL_USER_PATH, INDIVIDUAL_USER_PATH } from '@constants/routes';
import { useAppSelector } from '@store/hooks';
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { useTranslation } from 'react-i18next';
import Table from 'react-bootstrap/Table';
import UserDeviceList from './UserDeviceList';
import {
    APPROVED,
    BLOCKED,
    REJECTED,
    VERIFIED,
    WAITING,
} from '@constants/constants';
import { useNavigate } from 'react-router-dom';
import { getUserInfo, getUserName, updateUser } from '@utils/utils';
import { getUserAttributes } from '@utils/authUtils';
import { TENANT_ID } from '@constants/cognitoAttributes';
import ModalPopUp from '@components/ModalPopUpComponent';
import {
    capitalizeFirstLetter,
    formatDateDMY,
    generateUniqueTimestamp,
    getDataUsingUserId,
    sendStatusNotification,
} from '@utils/sharedUtils';
import DataLoader from '@components/DataLoader';
import { sendPushNotification } from '@utils/notificationUtils';
import { useAuditLog } from '../../contexts/AuditLogContext';

interface UserDetail {
    id: number;
    name: string;
    email: string;
    // Add any other user fields you need
}

type DeviceDetails = {
    device_id: string;
    name: string;
    date: string;
    id: string;
};

const UserDetailPage = () => {
    // const { id } = useParams<{ id: string }>();
    // const [userDetail, setUserDetail] = useState<UserDetail | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    // const [error, setError] = useState<string | null>(null);
    const [showBlockModal, setShowBlockModal] = useState(false);
    const [showUnBlockModal, setShowUnBlockModal] = useState(false);
    const userDetail = useAppSelector((state) => state.user.userDetails);
    const userDevice =
        useAppSelector((state) => state.user.userDeviceList) || [];
    const [userStatus, setUserStatus] = useState<string>(
        userDetail?.status ? userDetail?.status : ''
    );
    const { t } = useTranslation();
    const [deviceDetails, setDeviceDetails] = useState<DeviceDetails[]>([]);
    const navigate = useNavigate();
    const loggedInUserId = useAppSelector(
        (state) => state.login.loggedInUserID
    );
    const { addLog } = useAuditLog();
    const loggedInUserEmail = useAppSelector(
        (state) => state.login.loggedInUserEmail
    );

    useEffect(() => {
        if (!userDetail) {
            const pathname = location.pathname;
            // Check if the pathname matches any of the paths from AppConfig
            if (pathname.startsWith(HOSPITAL_USER_PATH + '/')) {
                navigate(HOSPITAL_USER_PATH);
            } else if (pathname.startsWith(INDIVIDUAL_USER_PATH + '/')) {
                navigate(INDIVIDUAL_USER_PATH);
            }
        }
        fetchDeviceDetails();
    }, [location.pathname, userDetail, navigate]);

    const fetchDeviceDetails = async () => {
        try {
            if (userDetail) {
                const individual = await getUserName(
                    'individual_device',
                    userDetail?.tenantUUid,
                    'tenant_id'
                );
                let assigned = [];
                if (individual) {
                    for (const device of individual) {
                        const deviceData = await getUserName(
                            'devices',
                            device?.device_id?.S ? device?.device_id?.S : '',
                            'uuid'
                        );
                        if (deviceData) {
                            const assignedDevice = deviceData[0];
                            const data = {
                                name: assignedDevice?.device_name?.S
                                    ? capitalizeFirstLetter(
                                          assignedDevice?.device_name?.S
                                      )
                                    : '',
                                device_id: assignedDevice?.device_id?.S
                                    ? capitalizeFirstLetter(
                                          assignedDevice?.device_id?.S
                                      )
                                    : '',
                                date: assignedDevice?.created_at?.S
                                    ? formatDateDMY(
                                          assignedDevice?.created_at?.S
                                      )
                                    : '',
                                id: assignedDevice?.uuid?.S
                                    ? assignedDevice?.uuid?.S
                                    : '',
                            };
                            assigned.push(data);
                        }
                    }
                    setDeviceDetails(assigned);
                }
            }
        } catch (error: any) {
            // setError(error.message);
            console.log('error:', error.message);
        } finally {
            setLoading(false);
        }
    };

    // useEffect(() => {
    //     const fetchUserDetail = async () => {
    //         try {
    //             const response = await fetch(`/api/users/${id}`);
    //             if (!response.ok) {
    //                 throw new Error('Network response was not ok');
    //             }
    //             const data = await response.json();
    //             setUserDetail(data);
    //         } catch (error) {
    //             setError(error.message);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     fetchUserDetail();
    // }, [id]);

    // if (loading) return <div>Loading...</div>;
    // if (error) return <div>Error: {error}</div>;
    // if (!userDetail) return <div>No user found</div>;

    const now = new Date();
    // const formattedDateTime = now.toLocaleString();
    const formattedDateTime = generateUniqueTimestamp();

    const updateStatus = async (status: string) => {
        try {
            const attributes = await getUserAttributes();

            if (typeof attributes === 'string' || attributes === null) {
                return;
            }

            const tenantUuid = attributes[TENANT_ID] ?? '';
            const attributeNames = {
                '#status': 'status',
                '#modified_by': 'modified_by',
                '#modified_at': 'modified_at',
            };

            const attributeValues = {
                ':status': { S: status },
                ':modified_by': { S: tenantUuid },
                ':modified_at': { S: formattedDateTime },
            };

            const updateExpression =
                'SET #status = :status, #modified_by = :modified_by, #modified_at = :modified_at';

            if (!userDetail?.tenantUUid) {
                return;
            }

            // const info = await getUserInfo('user_info', userDetail.tenantUUid);
            const info = await getDataUsingUserId('user_info', userDetail.id);
            if (!info || !info.Items) {
                return;
            }
            const uuid = info?.Items[0]?.uuid?.S;

            if (uuid) {
                await updateUser(
                    'user_info',
                    uuid,
                    attributeNames,
                    attributeValues,
                    updateExpression
                );
                setUserStatus(status);

                addLog(
                    'User Status Updated',
                    uuid,
                    'User Info',
                    loggedInUserId,
                    { 'Status': status, 'User': info?.Items[0]?.email?.S },
                    window.location.hostname
                );
            }
            const email = info?.Items?.[0]?.email?.S;
            const toAddress = email ? email : '';
            if (status.toLowerCase() === BLOCKED.toLowerCase()) {
                // await sendPushNotification({user_id: userDetail.id ? userDetail.id : '', type: 'blocked', device_id: '', reason: '', assigned_by: loggedInUserId });
                const notificationData = {
                    users: toAddress,
                    status: 'blocked',
                };

                // Send the data to the sharedUtils.ts function
                await sendStatusNotification(notificationData);
            } else if (status.toLowerCase() === APPROVED.toLowerCase()) {
                // await sendPushNotification({user_id: userDetail.id ? userDetail.id : '', type: 'approved', device_id: '', reason: '', assigned_by: loggedInUserId });
                const notificationData = {
                    users: toAddress,
                    status: 'approved',
                };

                // Send the data to the sharedUtils.ts function
                await sendStatusNotification(notificationData);
            } else if (status.toLowerCase() === REJECTED.toLowerCase()) {
                // await sendPushNotification({user_id: userDetail.id ? userDetail.id : '', type: 'rejected', device_id: '', reason: '', assigned_by: loggedInUserId });
                const notificationData = {
                    users: toAddress,
                    status: 'rejected',
                };

                // Send the data to the sharedUtils.ts function
                await sendStatusNotification(notificationData);
            }
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const getStatusClass = (status: string) => {
        // console.log('Status:', status);
        switch (status.toLowerCase().trim()) {
            case 'verified':
                return 'text-white bg-success inline-block rounded-2xl px-3 py-1';
            case 'not verified':
                return 'text-white bg-notverified inline-block rounded-2xl px-3 py-1';
            case BLOCKED.toLowerCase():
                return 'text-white bg-secondary inline-block rounded-2xl px-3 py-1';
            default:
                return '';
        }
    };

    return (
        <>
            {/* <div className="mt-5"> */}
            <div>
                <div className="flex justify-between">
                    <div className="font-bold text-black">
                        <Link to={INDIVIDUAL_USER_PATH}>
                            <div className="flex items-center">
                                <IoMdArrowRoundBack className="cursor-pointer" />{' '}
                                <div className="ml-2">{t('user_detail.individual')}</div>
                            </div>
                        </Link>
                    </div>
                    <div>
                        {userStatus.toLowerCase() == BLOCKED.toLowerCase() ? (
                            <button
                                className="text-primary py-1 px-3 border border-primary"
                                onClick={() => setShowUnBlockModal(true)}
                            >
                                {t('user_detail.unblock')}
                            </button>
                        ) : (
                            <button
                                className="text-primary py-1 px-3 border border-primary"
                                onClick={() => setShowBlockModal(true)}
                            >
                                {t('user_detail.block')}
                            </button>
                        )}
                    </div>
                </div>
                <div className="bg-white flex flex-col rounded mt-5">
                    <div className="text-end ml-2 mt-4 mb-2 mr-4">
                        <div
                            className={`text-base font-normal text-table_header ${userStatus ? getStatusClass(userStatus) : ''}`}
                        >
                            {capitalizeFirstLetter(userStatus)}
                        </div>
                    </div>
                    <Table>
                        <tbody>
                            <tr className="border-none">
                                <td className="border-none font-bold align-top">
                                    {t('user_detail.name')}
                                </td>
                                <td className="border-none text-black align-top">
                                    {userDetail?.name ? userDetail?.name : '--'}
                                </td>
                                <td className="border-none font-bold align-top">
                                    {t('user_detail.mobile_number')}
                                </td>
                                <td className="border-none text-black align-top">
                                    {userDetail?.contact_id
                                        ? userDetail?.contact_id
                                        : '--'}
                                </td>
                            </tr>
                            <tr className="border-none">
                                <td className="border-none font-bold align-top">
                                    {t('user_detail.created_on')}
                                </td>
                                <td className="border-none text-black align-top">
                                    {userDetail?.create_date
                                        ? userDetail?.create_date
                                        : '--'}
                                </td>
                                <td className="border-none font-bold align-top">
                                    {t('user_detail.email_id')}
                                </td>
                                <td className="border-none text-black align-top">
                                    {userDetail?.email_id
                                        ? userDetail?.email_id
                                        : '--'}
                                </td>
                            </tr>
                            <tr className="border-none">
                                <td className="border-none font-bold align-top">
                                    {t('user_detail.registered_device_id')}
                                </td>
                                <td className="border-none text-black align-top">
                                    {userDetail?.device_id
                                        ? userDetail?.device_id
                                        : '--'}
                                </td>
                                <td className="border-none font-bold align-top">
                                    {t('user_detail.address')}
                                </td>
                                <td className="border-none w-4/12 text-black align-top">
                                    {userDetail?.address
                                        ? userDetail?.address
                                        : '--'}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
                <div className="mt-5">
                    <div className="font-bold text-black">
                        <div className="flex items-center">
                            <div className="ml-2">
                                {t('user_detail.connected_devices_list')}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="bg-white flex flex-col rounded-lg mt-4 mb-4 w-[27rem] h-[29rem] relative">
                    <UserDeviceList deviceList={deviceDetails} />
                </div>
            </div>
            <ModalPopUp
                show={showBlockModal}
                message={t('pop_up.user_block')}
                onConfirm={() => {
                    updateStatus(BLOCKED);
                    setShowBlockModal(false);
                }}
                onCancel={() => setShowBlockModal(false)}
            />
            <ModalPopUp
                show={showUnBlockModal}
                message={t('pop_up.user_unblock')}
                onConfirm={() => {
                    updateStatus(
                        userDetail?.userEmailStatus
                            ? userDetail?.userEmailStatus
                            : VERIFIED
                    );
                    setShowUnBlockModal(false);
                }}
                onCancel={() => setShowUnBlockModal(false)}
            />
            {loading && <DataLoader />}
        </>
    );
};

export default UserDetailPage;
